import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';
import { ArrowLeft, CheckCircle, XCircle, Clock, DollarSign } from 'lucide-react';

const PaymentReportsAdminPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadReports();
  }, [filter]);

  const loadReports = async () => {
    try {
      const response = await api.get(`/api/superadmin/payment-reports${filter !== 'all' ? `?status=${filter}` : ''}`);
      setReports(response.data);
    } catch (error) {
      console.error('Error cargando reportes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (reportId, status) => {
    try {
      await api.put(`/api/superadmin/payment-reports/${reportId}/review`, {
        status,
        review_notes: reviewNotes
      });
      
      alert(`✅ Reporte ${status === 'approved' ? 'aprobado' : 'rechazado'} exitosamente`);
      setReviewModal(null);
      setReviewNotes('');
      loadReports();
    } catch (error) {
      console.error('Error revisando reporte:', error);
      alert('❌ Error al revisar reporte');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-500', text: 'Pendiente', icon: Clock },
      approved: { color: 'bg-green-500', text: 'Aprobado', icon: CheckCircle },
      rejected: { color: 'bg-red-500', text: 'Rechazado', icon: XCircle }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`${badge.color} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/super-admin')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'}`}
          >
            <ArrowLeft className={isDark ? 'text-white' : 'text-gray-900'} />
          </button>
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Reportes de Pago
            </h1>
            <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>
              Revisa y aprueba los pagos reportados por usuarios
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-3">
          {['pending', 'approved', 'rejected', 'all'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === status
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg'
                  : isDark
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {status === 'all' ? 'Todos' : status === 'pending' ? 'Pendientes' : status === 'approved' ? 'Aprobados' : 'Rechazados'}
            </button>
          ))}
        </div>

        {/* Reports List */}
        {reports.length === 0 ? (
          <div className={`text-center py-12 rounded-xl ${
            isDark ? 'bg-gray-800/50' : 'bg-white'
          } shadow-lg`}>
            <DollarSign className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <h3 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              No hay reportes {filter !== 'all' ? filter === 'pending' ? 'pendientes' : filter === 'approved' ? 'aprobados' : 'rechazados' : ''}
            </h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {reports.map((report) => (
              <div
                key={report.id}
                className={`p-6 rounded-xl shadow-lg border-2 ${
                  isDark
                    ? 'bg-gray-800/50 border-gray-700'
                    : 'bg-white border-gray-200'
                }`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {report.user_name || report.user_email}
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {report.user_email}
                    </p>
                  </div>
                  {getStatusBadge(report.status)}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Tipo:</span>
                    <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {report.payment_type === 'plan' ? '📋 Plan' : '🛠️ Paquete Herramientas'}
                    </span>
                  </div>
                  {report.plan_type && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Plan:</span>
                      <span className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {report.plan_type} ({report.payment_mode})
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Monto:</span>
                    <span className={`text-xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      ${report.amount}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Método:</span>
                    <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                      {report.payment_method === 'transfer' ? 'Transferencia' : report.payment_method === 'cash' ? 'Efectivo' : 'Tarjeta'}
                    </span>
                  </div>
                  {report.reference && (
                    <div className="flex justify-between">
                      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Referencia:</span>
                      <span className={`font-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {report.reference}
                      </span>
                    </div>
                  )}
                  {report.notes && (
                    <div className="mt-2">
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Notas:</span>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {report.notes}
                      </p>
                    </div>
                  )}
                  {report.proof_url && (
                    <div className="mt-3">
                      <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Comprobante:</span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${report.proof_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block mt-2"
                      >
                        {report.proof_url.endsWith('.pdf') ? (
                          <div className={`p-3 rounded-lg border text-center ${
                            isDark ? 'bg-red-900/20 border-red-700' : 'bg-red-50 border-red-300'
                          }`}>
                            <p className={`font-semibold ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                              📄 Ver Comprobante PDF
                            </p>
                          </div>
                        ) : (
                          <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${report.proof_url}`}
                            alt="Comprobante de pago"
                            className="max-w-full h-auto rounded-lg border-2 border-blue-500 hover:scale-105 transition-transform cursor-pointer"
                          />
                        )}
                      </a>
                    </div>
                  )}
                </div>

                <p className={`text-xs mb-4 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Reportado: {new Date(report.created_at).toLocaleDateString('es-MX', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>

                {report.status === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setReviewModal({...report, action: 'approved'})}
                      className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprobar
                    </button>
                    <button
                      onClick={() => setReviewModal({...report, action: 'rejected'})}
                      className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <XCircle className="w-4 h-4" />
                      Rechazar
                    </button>
                  </div>
                )}

                {report.status !== 'pending' && report.review_notes && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    isDark ? 'bg-gray-700/50' : 'bg-gray-100'
                  }`}>
                    <p className={`text-xs font-semibold mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Nota del administrador:
                    </p>
                    <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {report.review_notes}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Review Modal */}
        {reviewModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-md w-full rounded-xl shadow-2xl ${
              isDark ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="p-6">
                <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {reviewModal.action === 'approved' ? '✅ Aprobar' : '❌ Rechazar'} Pago
                </h2>

                <div className={`mb-4 p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Usuario:</p>
                  <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{reviewModal.user_email}</p>
                  <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Monto:</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>${reviewModal.amount}</p>
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Notas (opcional)
                  </label>
                  <textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    rows="3"
                    className={`w-full px-4 py-2 rounded-lg border ${
                      isDark 
                        ? 'bg-gray-700 border-gray-600 text-white' 
                        : 'bg-white border-gray-300 text-gray-900'
                    }`}
                    placeholder="Comentarios sobre esta decisión..."
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setReviewModal(null);
                      setReviewNotes('');
                    }}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold ${
                      isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-white'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                    }`}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleReview(reviewModal.id, reviewModal.action)}
                    className={`flex-1 px-4 py-2 rounded-lg font-semibold text-white ${
                      reviewModal.action === 'approved'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReportsAdminPage;
